import { TaskResult, Task } from '@/types';

/**
 * Calculates scores for task results, using the same logic as the EvaluationDetail component.
 * This handles both numerical and binary scoring methods correctly.
 * 
 * @param results - The task results to calculate scores for
 * @returns An object containing the total score, max possible score, and percentage
 */
export const calculateCategoryScore = (results: TaskResult[]) => {
  // Filter out results with null scores
  const evaluatedResults = results.filter(result => result.score !== null);
  
  if (evaluatedResults.length === 0) {
    return {
      totalScore: 0,
      maxPossibleScore: 0,
      percentage: 0,
      displayText: "Not evaluated"
    };
  }

  const totalScore = evaluatedResults.reduce((sum, result) => {
    const resultScore = typeof result.score === 'number' ? result.score : (result.score ? 1 : 0);
    return sum + resultScore;
  }, 0);

  const maxPossibleScore = evaluatedResults.reduce((sum, result) => {
    const task = result.task as Task;
    if (task.scoringMethod === 'numerical' && task.scoringScale) {
      return sum + task.scoringScale.max;
    }
    return sum + 1; // For binary scoring, max is 1
  }, 0);

  const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
  
  return {
    totalScore,
    maxPossibleScore,
    percentage,
    displayText: `${totalScore}/${maxPossibleScore} (${percentage}%)`
  };
};

/**
 * Calculates overall score for an evaluation.
 * Can be used in the EvaluationList and other components.
 * 
 * @param results - All results in the evaluation
 * @returns The calculated percentage score
 */
export const calculateOverallPercentage = (results: TaskResult[]): number => {
  const { percentage } = calculateCategoryScore(results);
  return percentage;
}; 